/* eslint-disable camelcase */
import { createSlice } from '@reduxjs/toolkit';
// utils
import axios from '../../utils/axios';
//
import { dispatch } from '../store';
import conObj from '../../utils/connection-assistant';
import { openAddMemberBankDialog } from './addMemberBankDialog';
import { getFingerprint, getLocalId } from '../../utils/formatObject';

// ----------------------------------------------------------------------

const initialState = {
  inbox: [],
  unreadInboxCount: 0,
  memberBalance: {
    currencyCode: '',
    amount: 0,
    balance: 0,
    displayBalance: 0,
  },
  memberBankInfo: {
    bank: '',
    bankAccountNumber: '',
  },
  gameCategsAndCompanies: [],
  gameCategs: [],
  siteSettings: JSON.parse(localStorage.getItem('site_configs') || '{}') || {},
  promotions: { data: [], categories: [] },
  angPauPromos: { data: [], isFetchedOnce: false },
  fakePlayerStats: [],
  transactionPlayerStats: [],
  withdrawRequirement: {
    calculationType: '',
    current: 0,
    requirement: 0,
    isSufficient: false,
    allowWithdraw: false,
  },
  depositWithdrawMinMax: {
    maxWithdrawLimit: 0,
    minWithdrawLimit: 0,
    currencyWithdraw: {},
    currencyTopup: {},
  },
};

const slice = createSlice({
  name: 'lookup',
  initialState,
  reducers: {
    setMemberBalance(state, action) {
      state.memberBalance = action.payload;
    },
    setInbox(state, action) {
      state.inbox = action.payload;
    },
    setUnreadInboxCount(state, action) {
      state.unreadInboxCount = action.payload;
    },
    setGameCategsAndCompanies(state, action) {
      state.gameCategsAndCompanies = action.payload;
    },
    setGameCategs(state, action) {
      state.gameCategs = action.payload;
    },
    setSiteSettings(state, action) {
      state.siteSettings = action.payload;
    },
    setPromotions(state, action) {
      state.promotions = action.payload;
    },
    claimPromotion(state, action) {
      const newPromotions = JSON.parse(JSON.stringify(state.promotions?.data));

      if (newPromotions?.length) {
        state.promotions = {
          ...state.promotions,
          data: newPromotions.map((x) => {
            if (action.payload === x?.id) {
              return { ...x, is_claimable_now: false };
            }

            return x;
          }),
        };
      }
    },
    claimPromotionBanner(state, action) {
      const newBanner = JSON.parse(JSON.stringify(state.siteSettings?.sliders));

      if (newBanner?.length) {
        state.siteSettings = {
          ...state.siteSettings,
          sliders: newBanner.map((x) => {
            if (action.payload === x?.BannerSlide?.id) {
              return {
                BannerSlide: {
                  ...x?.BannerSlide,
                  is_claimable_now: false,
                },
              };
            }

            return x;
          }),
        };
      }
    },
    setAngPauPromos(state, action) {
      state.angPauPromos = action.payload;
    },
    setFakePlayerStats(state, action) {
      state.fakePlayerStats = action.payload;
    },
    setTransactionStats(state, action) {
      state.transactionStats = action.payload;
    },
    setWithdrawRequirement(state, action) {
      state.withdrawRequirement = action.payload;
    },
    setMemberBankInfo(state, action) {
      state.memberBankInfo = action.payload;
    },
    setDepositWithdrawMinMax(state, action) {
      state.depositWithdrawMinMax = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  setMemberBalance,
  setInbox,
  setUnreadInboxCount,
  setGameCategsAndCompanies,
  setGameCategs,
  setSiteSettings,
  setPromotions,
  claimPromotion,
  claimPromotionBanner,
  setAngPauPromos,
  setFakePlayerStats,
  setTransactionStats,
  setWithdrawRequirement,
  setMemberBankInfo,
  setDepositWithdrawMinMax,
} = slice.actions;

const setUserBalance = (response) => {
const {
    balance,
    data: _memberBank,
    maxWithdrawLimit,
    minWithdrawLimit,
    currencyTopup,
    currencyWithdraw,
  } = response?.data;

  dispatch(
    setMemberBalance({
      currencyCode: balance?.currency,
      balance: balance?.balance,
      displayBalance: balance?.display_balance,
      canClearBalance: balance?.can_clear_balance,
    })
  );
  dispatch(setWithdrawRequirement(response?.data?.withdrawRequirement));
  if (_memberBank?.length > 0) {
    dispatch(
      setMemberBankInfo({
        bank: _memberBank?.[0]?.Bank?.name,
        bankAccountNumber: _memberBank?.[0]?.account_number,
      })
    );
    dispatch(
      setDepositWithdrawMinMax({
        maxWithdrawLimit,
        minWithdrawLimit,
        currencyTopup,
        currencyWithdraw,
      })
    );
  } else {
    dispatch(openAddMemberBankDialog({ open: true }));
  }
}

export async function getUserMemberBalance() {
  const { fpResult, visitorId } = await getFingerprint();
  const localId = await getLocalId();

  const response = await conObj.post('members/banks.json', {
    fingerprint: visitorId,
    fingerprint_data: fpResult,
    local_id: localId,
  });

  setUserBalance(response.data);

  return response;
}

// export function getUserMemberBalance() {
//   return conObj.post('members/balance.json').then((response) => {
//     const { currency, display_balance: displayBalance, balance } = response.data?.data?.data;
//     dispatch(setMemberBalance({ currencyCode: currency, balance, displayBalance }));
//   });
// }

export function getInbox(page = 0, limit = 20) {
  return conObj.post(`notifications.json?page=${page}&limit=${limit}`).then((response) => {
    dispatch(setInbox(response.data.data.data));
  });
}

export function getUnreadInboxCount(page = 0, limit = 20) {
  return conObj.post(`unread_notifications.json?page=${page}&limit=${limit}`).then((response) => {
    dispatch(setUnreadInboxCount(response.data.data.data.count));
  });
}

export function getGameCategsAndCompanies() {
  return conObj.post('games/game_categories.json').then((response) => {
    dispatch(setGameCategsAndCompanies(response?.data?.data?.data));
  });
}

export function getSiteSettings() {
  return conObj.post('setting/site_configs.json').then((response) => {
    saveSiteSettings(response?.data);
  });
}

const saveSiteSettings = (response) => {
  const {
    configs,
    popups,
    announcement_text,
    sliders,
    languages,
    member_group_banners,
    referral_banners,
    currencyWithdraw,
    currencyTopup,
  } = response?.data?.data;
  let _popupBanner = [];

  if (popups?.length > 0) {
    _popupBanner = popups.map((x) => ({
      showPopup: true,
      ...x?.BannerSlide,
    }));
  }

  const _siteSettings = {
    ...configs,
    announcement_text,
    sliders,
    languages,
    memberGroupBanners: member_group_banners,
    referralBanners: referral_banners,
    popups: _popupBanner,
    currencyWithdraw,
    currencyTopup,
  };
  dispatch(setSiteSettings(_siteSettings));
  localStorage.setItem('site_configs', JSON.stringify(_siteSettings));
};

export function getPromotions() {
  return conObj.post('promotion.json').then((response) => {
    dispatch(
      setPromotions({
        data: response.data?.data?.data,
        categories: response.data?.data?.categories,
        groupings: response.data?.data?.groupings,
      })
    );
  });
}

export function getAngPauPromotion() {
  return conObj
    .post('promotion.json', {
      promotion_type: 'angpau_manual',
    })
    .then((response) => {
      const respData = response.data?.data?.data;
      if (respData) dispatch(setAngPauPromos({ data: respData, isFetchedOnce: true }));
    });
}

export function getFakePlayerStats() {
  return conObj.post('setting/player_stats.json').then((response) => {
    if (response.data?.data?.data) {
      dispatch(setFakePlayerStats(response?.data?.data?.data?.latest_player_wins));
    }
  });
}

export function getTransactionStats() {
  return conObj.post('setting/transaction_stats.json').then((response) => {
    if (response.data?.data?.data) {
      dispatch(setTransactionStats(response?.data?.data?.data));
    }
  });
}

export async function multiRequest() {
  let memberResult = null;
  const { fpResult, visitorId } = await getFingerprint();
  const localId = await getLocalId();

  const isAuthenticated = localStorage.getItem('accessToken');

  const requestsData = {
    'requests[0]': JSON.stringify({ url: 'setting/site_configs.json', extra_input: '{}' }),
    'requests[1]': JSON.stringify({ url: 'games/game_categories.json', extra_input: '{}' }),
    fingerprint: visitorId,
    fingerprint_data: fpResult,
    local_id: localId,
  };

  if (isAuthenticated) {
    requestsData['requests[2]'] = JSON.stringify({ url: 'members/banks.json', extra_input: '{}' });
    requestsData['requests[3]'] = JSON.stringify({ url: 'profile.json', extra_input: '{}' });
  }

  await conObj.post('multi.json', requestsData).then((response) => {
    const multiResponse = response?.data?.data?.multiResults;

    if (multiResponse) {
      const profileResponse = multiResponse['profile.json'];
      if (profileResponse) memberResult = profileResponse;

      const siteSettings = multiResponse['setting/site_configs.json'];
      if (siteSettings) saveSiteSettings(siteSettings);
      
      const gameCategsAndCompanies = multiResponse['games/game_categories.json'];
      if (gameCategsAndCompanies) dispatch(setGameCategsAndCompanies(gameCategsAndCompanies?.data?.data));

      const bankData = multiResponse["members/banks.json"];
      if(bankData) setUserBalance(bankData);
    }
  });
  return memberResult; // banks response
}
